/**
 * DiscoveryCards Component
 * ========================
 * Three discovery entry points
 * ALL content from Blueprint - NO hardcoded text
 * 
 * Expected config format (ui.home.discovery):
 * {
 *   title: "Esplora il sistema",
 *   cards: [
 *     { title, description, route, icon, image }
 *   ]
 * }
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { Search, Image, Folder, ArrowRight, Grid3X3 } from 'lucide-react';

// Icon mapping
const iconMap = {
  search: Search,
  image: Image,
  folder: Folder,
  grid: Grid3X3
};

const DiscoveryCards = () => {
  const { homepage, getRoute } = useBlueprint();
  const discovery = homepage?.discovery || {};
  const cards = discovery?.cards || [];
  
  // Safe text extraction helper
  const safeText = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.text) return safeText(value.text, fallback);
      if (value.label) return safeText(value.label, fallback);
      return fallback;
    }
    return fallback;
  };
  
  // Resolve route - check if it's a route key or direct path
  const resolveRoute = (route) => {
    if (!route) return '/';
    if (typeof route === 'object') return route.route || route.url || '/';
    if (route.startsWith('/')) return route;
    return getRoute(route, `/${route}`);
  };
  
  // Safe discovery title
  const discoveryTitle = safeText(discovery.title, '');
  
  // If no cards, don't render
  if (!cards.length) {
    return null;
  }
  
  return (
    <BlueprintSection name="discovery">
      <section className="bg-[var(--cream)]">
        <div className="px-6 lg:px-[88px] lg:pr-[28%] -mt-24 relative z-10">
          {discoveryTitle && (
            <p className="sr-label mb-4">
              {discoveryTitle}
            </p>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {cards.map((card, i) => {
              const IconComponent = iconMap[card.icon] || Grid3X3;
              const cardRoute = resolveRoute(card.route || card.link);
              
              return (
                <Link
                  key={card.id || i}
                  to={cardRoute}
                  className="sr-card flex flex-col"
                >
                  <div className="flex-1 p-5 pb-3.5">
                    {/* Icon */}
                    <div className="w-[34px] h-[34px] rounded-full border border-[#E8E3DC] bg-[#FAFAF8] flex items-center justify-center mb-3">
                      <IconComponent size={14} className="text-[var(--gold)]" />
                    </div>
                    
                    {/* Title */}
                    <p className="text-[10.5px] font-bold tracking-[1.8px] uppercase text-[var(--dark)] mb-1.5">
                      {safeText(card.title, 'Card')}
                    </p>
                    
                    {/* Description */}
                    <p className="text-[12.5px] text-[#555] leading-[1.6] mb-3">
                      {safeText(card.description, '')}
                    </p>
                    
                    {/* Link arrow */}
                    <span className="text-xs font-bold text-[var(--gold)] inline-flex items-center gap-1.5">
                      <ArrowRight size={11} />
                    </span>
                  </div>
                  
                  {/* Image (optional) */}
                  {card.image && (
                    <div className="h-[82px] overflow-hidden flex-shrink-0">
                      <img
                        src={card.image}
                        alt={safeText(card.title, '')}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
        
        {/* Spacer */}
        <div className="h-[60px] bg-[var(--cream)]" />
      </section>
    </BlueprintSection>
  );
};

export default DiscoveryCards;
